import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { TABLE_CELL_PADDING_CLASS } from '../table-tokens'
import { SkeletonRegion, useStableKeys } from './_shared'

export interface TableSkeletonProps {
	rows?: number
	cols?: number
	showCheckbox?: boolean
	showPagination?: boolean
	colWidths?: string[]
	className?: string
	label?: string | false
}

const CELL_WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-4/5', 'w-1/2', 'w-3/5']
const CELL_MAX_WIDTHS = ['max-w-48', 'max-w-40', 'max-w-56', 'max-w-44', 'max-w-52', 'max-w-36']

const OUTER = 'flex size-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden border bg-card'
const INNER = 'flex size-full min-w-0 flex-col bg-background'
const HEADER_BAND = 'shrink-0 border-b bg-table-header-bg'
const HEADER_ROW = 'flex h-table-header items-center text-body'
const BODY = 'min-h-0 flex-1 overflow-hidden bg-card'
const BODY_ROW = 'flex h-table-row items-center bg-table-row-bg'

const SELECT_CELL = 'flex w-[30px] shrink-0 items-center justify-center'
const HEADER_CELL = `flex shrink-0 items-center ${TABLE_CELL_PADDING_CLASS}`
const BODY_CELL = `flex shrink-0 items-center ${TABLE_CELL_PADDING_CLASS}`
const COLUMN_WIDTH = 'w-48'

export function TableSkeleton({
	rows = 20,
	cols = 5,
	showCheckbox = false,
	showPagination = false,
	colWidths,
	className,
	label = 'Loading data',
}: TableSkeletonProps) {
	const rowKeys = useStableKeys(rows)
	const colKeys = useStableKeys(Math.max(1, cols))
	const lastCol = colKeys.length - 1

	return (
		<SkeletonRegion label={label} className={cn(OUTER, className)}>
			<div className={INNER}>
				<div className={HEADER_BAND}>
					<div className={HEADER_ROW}>
						{showCheckbox ? (
							<div className={SELECT_CELL}>
								<Skeleton className='size-4 rounded-sm' />
							</div>
						) : null}
						{colKeys.map((key, c) => (
							<div key={key} className={cn(HEADER_CELL, c === lastCol ? 'flex-1' : COLUMN_WIDTH)}>
								<Skeleton className='h-4 w-20' />
							</div>
						))}
					</div>
				</div>

				<div className={BODY}>
					{rowKeys.map((rowKey, r) => (
						<div key={rowKey} className={BODY_ROW}>
							{showCheckbox ? (
								<div className={SELECT_CELL}>
									<Skeleton className='size-4 rounded-sm' />
								</div>
							) : null}
							{colKeys.map((colKey, c) => (
								<div key={colKey} className={cn(BODY_CELL, c === lastCol ? 'flex-1' : COLUMN_WIDTH)}>
									<Skeleton
										className={cn(
											'h-4',
											CELL_MAX_WIDTHS[(r + c) % CELL_MAX_WIDTHS.length],
											colWidths?.[c % colWidths.length] ?? CELL_WIDTHS[(r + c) % CELL_WIDTHS.length],
										)}
									/>
								</div>
							))}
						</div>
					))}
				</div>
			</div>

			{showPagination ? (
				<div className='flex shrink-0 items-center justify-between border-t px-3 py-2'>
					<Skeleton className='h-4 w-32' />
					<div className='flex gap-2'>
						<Skeleton className='h-control w-16' />
						<Skeleton className='h-control w-16' />
					</div>
				</div>
			) : null}
		</SkeletonRegion>
	)
}
